import React, { useCallback, useEffect, useState } from "react";

import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import Alert from "@busbud/design-system-components/dist/Alert";
import CircularProgress from "@busbud/design-system-components/dist/CircularProgress";
import Divider from "@busbud/design-system-components/dist/Divider";
import Stack from "@busbud/design-system-components/dist/Stack";

import { useAppContext } from "@app/helpers/hooks";
import type { CreditsActivity as CreditsActivityType } from "@app/types/loyalty";
import type { NapiUser } from "@app/types/napi-types";

import type { Dispatch } from "@reduxjs/toolkit";

import { CreditsActivityList } from "./credits-activity-list";
import { useStyles } from "./credits-activity.styles";
import { TransactionsFilter } from "./transactions-filter";
import { closeModal } from "../../../store/state/modals";
import { AlertDialog } from "../../alert-dialog";

interface ReduxProps {
  closeModal: () => void;
  user: NapiUser;
}

// The launch of loyalty program
const DEFAULT_START_YEAR = 2021;
const CURRENT_YEAR = new Date().getFullYear();

const getMostRecentYear = (default_year: number, user: NapiUser) => {
  const user_created_year = new Date(user.created_at).getFullYear();

  return Math.max(default_year, user_created_year);
};

const CreditsActivityComponent: React.FC<ReduxProps> = ({
  closeModal,
  user
}) => {
  const { api, device } = useAppContext();
  const [is_loading, setLoading] = useState(true);
  const [has_error, setError] = useState(false);
  const [credits_activity, setCreditsActivity] = useState<
    CreditsActivityType[]
  >([]);
  const { classes } = useStyles();
  const most_recent_year = getMostRecentYear(DEFAULT_START_YEAR, user);
  const [filter_start_year, set_filter_start_year] =
    useState<number>(CURRENT_YEAR);

  const getCreditsActivity = useCallback(
    async (year: number) => {
      setLoading(true);
      setError(false);
      try {
        const credits_activity = await api.getCreditsActivity(year);
        setCreditsActivity(credits_activity);
      } catch (err) {
        // todo tracking
        setError(true);
      }
      setLoading(false);
    },
    [api]
  );

  const handleYearSelected = (year: number) => {
    set_filter_start_year(year);
  };

  useEffect(() => {
    getCreditsActivity(filter_start_year);
  }, [filter_start_year, getCreditsActivity]);

  const getContent = () => {
    if (is_loading) {
      return <CircularProgress className={classes.progress} />;
    }

    return (
      <Stack direction="column" spacing={2.5}>
        <Stack direction="column" spacing={2}>
          <TransactionsFilter
            start_year={most_recent_year}
            selected_year={filter_start_year}
            onYearSelected={handleYearSelected}
          />
          <Divider />
        </Stack>
        {has_error ? (
          <Alert severity="negative">
            There was an error while fetching results. Please try again later.
          </Alert>
        ) : (
          <CreditsActivityList credits_activity={credits_activity} />
        )}
      </Stack>
    );
  };

  return (
    <AlertDialog
      title="Credits activity"
      onClose={() => closeModal()}
      message={getContent()}
      fullScreen={device.is_mobile}
    />
  );
};

const mapStateToProps = (state: { user: NapiUser }): { user: NapiUser } => {
  return { user: state.user };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ closeModal }, dispatch);

export const CreditsActivity = connect(
  mapStateToProps,
  mapDispatchToProps
)(CreditsActivityComponent);
